/**
 * AdvisorPage — Track advisor recommendations and performance
 *
 * Shows per-advisor scorecards with win rate, avg return, and a full
 * table of every recommendation. Comparison panel when multiple advisors.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  UserCheck, Plus, X, TrendingUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'

// ─── backend contract types (normalised here, not in lib/api) ────────────────
// The backend scorecard emits *_pct suffixed stats and stores recommendation
// rows with status 'open'/'closed'/'expired' + created_at (no `date` field).

interface ScorecardRow {
  advisor_name: string
  total_recommendations: number
  scored_recommendations?: number | null
  wins?: number | null
  win_rate_pct?: number | null
  avg_return_pct?: number | null
  best_call_pct?: number | null
  worst_call_pct?: number | null
  open_count?: number | null
  closed_count?: number | null
  verdict?: string | null
}

interface AdvisorRecRow {
  id: number
  advisor_name: string
  recommendation?: string | null
  ticker?: string | null
  action?: string | null
  rationale?: string | null
  price_at_recommendation?: number | null
  current_price?: number | null
  outcome_pct?: number | null
  status?: string | null
  created_at?: string | null
  notes?: string | null
}

/** POST /api/advisor/recommendation body — RecommendationCreate on the backend. */
interface RecommendationPayload {
  advisor_name: string
  recommendation: string
  ticker?: string
  action?: string
  rationale?: string
  price_at_recommendation?: number
}

// ─── helpers ────────────────────────────────────────────────────────────────

function pct(n: number | null | undefined, decimals = 1): string {
  if (n == null) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(decimals)}%`
}

function outcomeColor(outcome: number | null | undefined): string {
  if (outcome == null) return 'text-text-muted'
  if (outcome > 0) return 'text-emerald-400'
  if (outcome < 0) return 'text-rose-light'
  return 'text-text-muted'
}

/** Backend stores status 'open'/'closed'/'expired'; win/loss is derived from
 *  the sign of outcome_pct once a recommendation is closed. */
function statusIcon(status: string | null | undefined, outcomePct: number | null | undefined) {
  const s = (status ?? '').toLowerCase()
  if (s === 'closed' || s === 'expired') {
    if (outcomePct != null && outcomePct > 0) return <span className="text-emerald-400" title="Win">✅</span>
    if (outcomePct != null && outcomePct < 0) return <span className="text-rose-light" title="Loss">❌</span>
    return <span className="text-text-muted" title="Closed — no outcome recorded">—</span>
  }
  return <span className="text-amber-400" title="Open">⏳</span>
}

function winRateColor(rate: number): string {
  if (rate >= 60) return 'text-emerald-400'
  if (rate >= 45) return 'text-amber-400'
  return 'text-rose-light'
}

function verdict(scorecard: ScorecardRow): string {
  const winRate = scorecard.win_rate_pct
  const avgRet = scorecard.avg_return_pct ?? 0
  if (scorecard.total_recommendations === 0) return 'No recommendations logged yet.'
  if (winRate == null)
    return `No scored recommendations for ${scorecard.advisor_name} yet — outcomes appear once entry and current prices are recorded.`
  if (winRate >= 65 && avgRet > 5)
    return `${scorecard.advisor_name} is performing well — strong win rate and above-average returns.`
  if (winRate >= 55 && avgRet > 0)
    return `${scorecard.advisor_name} is doing reasonably well, slightly more winners than losers.`
  if (winRate >= 45)
    return `${scorecard.advisor_name} is around break-even. Worth monitoring closely.`
  return `${scorecard.advisor_name} is underperforming — more losses than wins. Consider reviewing their recommendations carefully.`
}

// ─── Add Recommendation Modal ────────────────────────────────────────────────

// Must match the backend ActionType Literal exactly or the POST 422s.
const ACTIONS = ['BUY', 'SELL', 'HOLD', 'SWITCH', 'INCREASE', 'REDUCE'] as const

function AddRecommendationModal({
  advisorNames,
  onClose,
  onSubmit,
}: {
  advisorNames: string[]
  onClose: () => void
  onSubmit: (data: RecommendationPayload) => Promise<void>
}) {
  const [advisor,    setAdvisor]    = useState(advisorNames[0] ?? '')
  const [newAdvisor, setNewAdvisor] = useState('')
  const [ticker,     setTicker]     = useState('')
  const [action,     setAction]     = useState<typeof ACTIONS[number]>('BUY')
  const [rationale,  setRationale]  = useState('')
  const [entryPrice, setEntryPrice] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const effectiveAdvisor = newAdvisor.trim() || advisor

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!effectiveAdvisor || !ticker || !action) return
    setSubmitting(true)
    setSubmitError(null)
    const cleanTicker = ticker.toUpperCase().trim()
    try {
      await onSubmit({
        advisor_name:   effectiveAdvisor,
        // Backend requires a free-text `recommendation` summary line.
        recommendation: `${action} ${cleanTicker}${rationale ? ` — ${rationale}` : ''}`,
        ticker:         cleanTicker,
        action,
        rationale:      rationale || undefined,
        price_at_recommendation: entryPrice ? parseFloat(entryPrice) : undefined,
      })
      // Close only on success — failures keep the modal open with the error.
      onClose()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setSubmitError(
        typeof detail === 'string'
          ? detail
          : (err?.message ?? 'Failed to log recommendation'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="card p-6 w-full max-w-md space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="panel-title">Log Recommendation</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-surface-hover rounded" aria-label="Close">
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Advisor */}
        {advisorNames.length > 0 && (
          <div>
            <label htmlFor="ar-advisor" className="block text-sm text-text-secondary mb-1">Advisor</label>
            <select
              id="ar-advisor"
              value={advisor}
              onChange={e => setAdvisor(e.target.value)}
              className="input w-full"
            >
              {advisorNames.map(n => <option key={n} value={n}>{n}</option>)}
              <option value="">+ New advisor…</option>
            </select>
          </div>
        )}
        {(advisorNames.length === 0 || advisor === '') && (
          <div>
            <label htmlFor="ar-new-advisor" className="block text-sm text-text-secondary mb-1">
              {advisorNames.length === 0 ? 'Advisor Name' : 'New Advisor Name'}
            </label>
            <input
              id="ar-new-advisor"
              value={newAdvisor}
              onChange={e => setNewAdvisor(e.target.value)}
              placeholder="e.g. James at Hargreaves"
              className="input w-full"
              required={advisorNames.length === 0}
            />
          </div>
        )}

        {/* Ticker & action */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ar-ticker" className="block text-sm text-text-secondary mb-1">Ticker / Asset</label>
            <input
              id="ar-ticker"
              value={ticker}
              onChange={e => setTicker(e.target.value)}
              placeholder="AAPL"
              className="input w-full uppercase"
              required
            />
          </div>
          <div>
            <label htmlFor="ar-action" className="block text-sm text-text-secondary mb-1">Action</label>
            <select
              id="ar-action"
              value={action}
              onChange={e => setAction(e.target.value as typeof action)}
              className="input w-full"
            >
              {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* Entry price — the log date is recorded server-side (created_at) */}
        <div>
          <label htmlFor="ar-entry" className="block text-sm text-text-secondary mb-1">Entry Price (£)</label>
          <input
            id="ar-entry"
            type="number"
            value={entryPrice}
            onChange={e => setEntryPrice(e.target.value)}
            placeholder="optional"
            className="input w-full"
            min="0"
            step="0.01"
          />
        </div>

        {/* Rationale */}
        <div>
          <label htmlFor="ar-rationale" className="block text-sm text-text-secondary mb-1">Rationale</label>
          <textarea
            id="ar-rationale"
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            placeholder="Why did they recommend this?"
            className="input w-full h-20 resize-none"
          />
        </div>

        {submitError && (
          <div className="rounded-lg border border-rose/30 bg-rose/5 px-3 py-2">
            <p className="text-xs text-rose-light">{submitError}</p>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={submitting}>
            {submitting ? 'Saving…' : 'Log Recommendation'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ─── Advisor Scorecard Panel ─────────────────────────────────────────────────

function AdvisorPanel({
  scorecard,
  recommendations,
}: {
  scorecard: ScorecardRow
  recommendations: AdvisorRecRow[]
}) {
  const winRateNum = scorecard.win_rate_pct ?? 0

  return (
    <section className="card overflow-hidden" aria-label={`${scorecard.advisor_name} scorecard`}>
      {/* Advisor name header */}
      <div className="p-5 border-b border-border flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
          <UserCheck size={20} className="text-blue-400" aria-hidden />
        </div>
        <div>
          <h2 className="font-bold text-text-primary">{scorecard.advisor_name}</h2>
          <p className="text-xs text-text-muted">{scorecard.total_recommendations} recommendation{scorecard.total_recommendations !== 1 ? 's' : ''} logged</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border">
        <div className="bg-surface-card p-4">
          <p className="panel-title">Win Rate</p>
          <p className={clsx('stat-value mt-1 tabular-nums', winRateColor(winRateNum))}>
            {scorecard.win_rate_pct != null ? `${winRateNum.toFixed(0)}%` : '—'}
          </p>
        </div>
        <div className="bg-surface-card p-4">
          <p className="panel-title">Avg Return</p>
          <p className={clsx(
            'stat-value mt-1 tabular-nums',
            scorecard.avg_return_pct == null ? 'text-text-muted'
            : scorecard.avg_return_pct > 0 ? 'text-emerald-400'
            : scorecard.avg_return_pct < 0 ? 'text-rose-light'
            : 'text-text-muted',
          )}>
            {pct(scorecard.avg_return_pct)}
          </p>
        </div>
        <div className="bg-surface-card p-4">
          <p className="panel-title">Total Recs</p>
          <p className="stat-value mt-1 tabular-nums">
            {scorecard.total_recommendations}
          </p>
        </div>
        <div className="bg-surface-card p-4">
          <p className="panel-title">Best Call</p>
          <p className="stat-value text-emerald-400 mt-1 tabular-nums">
            {scorecard.best_call_pct != null ? pct(scorecard.best_call_pct) : '—'}
          </p>
        </div>
      </div>

      {/* Verdict */}
      <div className="px-5 py-3 bg-surface border-b border-border">
        <p className="text-sm text-text-secondary leading-relaxed">{verdict(scorecard)}</p>
      </div>

      {/* Recommendations table */}
      {recommendations.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-pro">
            <thead>
              <tr>
                <th>Date</th>
                <th>Ticker</th>
                <th>Action</th>
                <th className="hidden sm:table-cell">Rationale</th>
                <th className="!text-right">Outcome</th>
                <th className="!text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {recommendations.map(rec => (
                <tr key={rec.id}>
                  <td className="p-3 text-text-muted text-xs tabular-nums whitespace-nowrap">
                    {rec.created_at
                      ? new Date(rec.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                      : '—'}
                  </td>
                  <td className="p-3 font-mono font-semibold text-text-primary">{rec.ticker ?? '—'}</td>
                  <td className="p-3">
                    <span className={clsx(
                      'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded',
                      rec.action === 'BUY' || rec.action === 'INCREASE'
                        ? 'bg-emerald-500/15 text-emerald-400'
                        : rec.action === 'SELL' || rec.action === 'REDUCE'
                        ? 'bg-rose/15 text-rose-light'
                        : 'bg-amber-500/15 text-amber-400',
                    )}>
                      {rec.action ?? '—'}
                    </span>
                  </td>
                  <td className="p-3 text-text-muted text-xs hidden sm:table-cell max-w-xs">
                    <span className="line-clamp-2" title={rec.rationale ?? rec.recommendation ?? ''}>
                      {(() => {
                        const text = rec.rationale ?? rec.recommendation
                        return text ? text.slice(0, 80) + (text.length > 80 ? '…' : '') : '—'
                      })()}
                    </span>
                  </td>
                  <td className={clsx('p-3 text-right font-semibold tabular-nums text-xs', outcomeColor(rec.outcome_pct))}>
                    {pct(rec.outcome_pct)}
                  </td>
                  <td className="p-3 text-center">
                    {statusIcon(rec.status, rec.outcome_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="p-5 text-sm text-text-muted text-center">No recommendations logged for this advisor yet.</p>
      )}
    </section>
  )
}

// ─── Comparison Panel ────────────────────────────────────────────────────────

function ComparisonPanel({ scorecards }: { scorecards: ScorecardRow[] }) {
  const sorted = [...scorecards].sort((a, b) => (b.win_rate_pct ?? 0) - (a.win_rate_pct ?? 0))
  const best = sorted[0]

  return (
    <section className="card overflow-hidden" aria-label="Advisor comparison">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <TrendingUp size={15} className="text-blue-400" aria-hidden />
        <h2 className="panel-title">Advisor Comparison</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-pro">
          <thead>
            <tr>
              <th>Advisor</th>
              <th className="!text-right">Win Rate</th>
              <th className="!text-right">Avg Return</th>
              <th className="!text-right">Best Call</th>
              <th className="!text-right">Recs</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(sc => (
              <tr key={sc.advisor_name}>
                <td className="p-3 font-medium text-text-primary">
                  {sc.advisor_name}
                  {sc.advisor_name === best?.advisor_name && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                      Top
                    </span>
                  )}
                </td>
                <td className={clsx('p-3 text-right font-bold tabular-nums', winRateColor(sc.win_rate_pct ?? 0))}>
                  {sc.win_rate_pct != null ? `${sc.win_rate_pct.toFixed(0)}%` : '—'}
                </td>
                <td className={clsx('p-3 text-right tabular-nums', outcomeColor(sc.avg_return_pct))}>
                  {pct(sc.avg_return_pct)}
                </td>
                <td className="p-3 text-right text-emerald-400 tabular-nums">
                  {pct(sc.best_call_pct)}
                </td>
                <td className="p-3 text-right text-text-muted tabular-nums">
                  {sc.total_recommendations}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AdvisorPage() {
  const [scorecards, setScorecards]     = useState<ScorecardRow[]>([])
  const [recs,       setRecs]           = useState<AdvisorRecRow[]>([])
  const [loading,    setLoading]        = useState(true)
  const [error,      setError]          = useState<string | null>(null)
  const [showAdd,    setShowAdd]        = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [sc, r] = await Promise.all([
        api.get<ScorecardRow[]>('/api/advisor/scorecard'),
        api.get<AdvisorRecRow[]>('/api/advisor/recommendations'),
      ])
      setScorecards(sc.data ?? [])
      setRecs(r.data ?? [])
    } catch {
      setError('Failed to load advisor data. Make sure the backend is running.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async (body: RecommendationPayload) => {
    // Throws on failure — the modal catches, shows the error, and stays open.
    const { data: created } = await api.post<AdvisorRecRow>('/api/advisor/recommendation', body)
    setRecs(prev => [created, ...prev])
    // Refresh scorecards since win rate etc. may have changed
    try {
      const sc = await api.get<ScorecardRow[]>('/api/advisor/scorecard')
      setScorecards(sc.data ?? [])
    } catch { /* best-effort */ }
    setShowAdd(false)
  }

  const advisorNames = [...new Set(scorecards.map(sc => sc.advisor_name))]

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Advisor Scorecard"
        subtitle="Track every recommendation and hold your advisors to account"
        actions={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Plus size={16} aria-hidden />
            Log Recommendation
          </button>
        }
      />

      {/* ── Error ── */}
      {error && (
        <div className="card p-4 border border-rose/30 bg-rose/5">
          <p className="text-sm text-rose-light">{error}</p>
          <button type="button" onClick={load} className="btn-secondary text-xs mt-2">
            Retry
          </button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="space-y-5">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card p-5 space-y-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 skeleton" />
                <div className="space-y-2 flex-1">
                  <div className="h-3.5 skeleton w-1/3" />
                  <div className="h-2.5 skeleton w-1/5" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-px">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-20 skeleton" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Comparison (multi-advisor) ── */}
      {!loading && scorecards.length > 1 && (
        <ComparisonPanel scorecards={scorecards} />
      )}

      {/* ── Per-advisor panels ── */}
      {!loading && scorecards.length > 0 && (
        <div className="space-y-6 stagger-children">
          {scorecards.map(sc => (
            <AdvisorPanel
              key={sc.advisor_name}
              scorecard={sc}
              recommendations={recs.filter(r => r.advisor_name === sc.advisor_name)}
            />
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && scorecards.length === 0 && !error && (
        <div className="card p-12 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto">
            <UserCheck size={28} className="text-blue-400" aria-hidden />
          </div>
          <div>
            <p className="text-text-primary font-semibold">No recommendations logged yet</p>
            <p className="text-sm text-text-muted mt-1">
              Start tracking every recommendation your advisor makes. You'll quickly see who actually beats the market.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus size={16} aria-hidden />
            Log your first recommendation
          </button>
        </div>
      )}

      {/* ── Modal ── */}
      {showAdd && (
        <AddRecommendationModal
          advisorNames={advisorNames}
          onClose={() => setShowAdd(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  )
}
